/**
 * Coluna esquerda do editor: paleta de blocos e estrutura da página.
 *
 * Todo o arrastar-e-soltar mora aqui — o resto do editor não sabe que DnD
 * existe, só recebe "insira em i" / "mova de→para".
 *
 * Blocks are reordered by dragging in the outline, not on the canvas: dragging
 * a full-bleed section over another gives no usable drop target; the outline is
 * a list, and a list is the shape drag-and-drop actually works on. Os cards da
 * paleta são arrastados para a mesma lista — é assim que se escolhe *onde* o
 * bloco entra.
 */

import { ROTULOS_BLOCO, TIPOS_BLOCO, type Bloco, type TipoBloco } from '@egba-do/shared'
import { useRef, useState } from 'react'

/** O que está sendo arrastado: um card da paleta ou uma linha da estrutura. */
type Arrasto = { tipo: TipoBloco } | { de: number }

export interface PainelBlocosProps {
  blocos: Bloco[]
  selecionado: string | null
  problemasPorBloco: Map<string, string[]>
  onSelecionar: (id: string) => void
  onInserir: (tipo: TipoBloco, posicao?: number) => void
  onMover: (de: number, para: number) => void
  onDuplicar: (id: string) => void
  onAlternarOculto: (id: string) => void
  onRemover: (id: string) => void
}

export function PainelBlocos({
  blocos,
  selecionado,
  problemasPorBloco,
  onSelecionar,
  onInserir,
  onMover,
  onDuplicar,
  onAlternarOculto,
  onRemover,
}: PainelBlocosProps) {
  const arrastando = useRef<Arrasto | null>(null)
  const [sobre, setSobre] = useState<number | null>(null)

  function soltarEm(indice: number): void {
    const a = arrastando.current
    arrastando.current = null
    setSobre(null)
    if (!a) return
    if ('tipo' in a) onInserir(a.tipo, indice)
    else onMover(a.de, indice)
  }

  return (
    <aside className="grid min-h-0 grid-rows-[auto_minmax(0,1fr)] border-r border-[color:var(--color-borda)]">
      <div className="max-h-[40vh] overflow-y-auto p-4">
        <p className="mb-1 text-[11px] font-semibold tracking-wider text-slate-400 uppercase">
          Blocos
        </p>
        <p className="mb-2.5 text-xs text-slate-400">
          Clique para inserir depois do bloco selecionado, ou arraste até a estrutura.
        </p>
        <div className="space-y-1.5">
          {TIPOS_BLOCO.map((t) => (
            <button
              key={t}
              type="button"
              draggable
              onDragStart={(e) => {
                arrastando.current = { tipo: t }
                // O Firefox só inicia o arraste se houver dado no evento —
                // sem isto o card simplesmente não sai do lugar.
                e.dataTransfer.setData('text/plain', t)
                e.dataTransfer.effectAllowed = 'copy'
              }}
              onDragEnd={() => {
                arrastando.current = null
                setSobre(null)
              }}
              onClick={() => onInserir(t)}
              className="block w-full cursor-grab rounded-lg border border-[color:var(--color-borda)] px-2.5 py-2 text-left transition hover:border-marca-400 hover:bg-marca-50 active:cursor-grabbing"
            >
              <span className="block text-[13px] font-medium text-slate-700">
                {ROTULOS_BLOCO[t].nome}
              </span>
              <span className="mt-0.5 block text-[11px] leading-snug text-slate-400">
                {ROTULOS_BLOCO[t].descricao}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 overflow-y-auto border-t border-[color:var(--color-borda)] p-4">
        <p className="mb-2 text-[11px] font-semibold tracking-wider text-slate-400 uppercase">
          Estrutura da página
        </p>
        <ul className="space-y-1">
          {blocos.map((b, i) => {
            const problemasDoBloco = problemasPorBloco.get(b.id)
            return (
              <li
                key={b.id}
                draggable
                onDragStart={(e) => {
                  arrastando.current = { de: i }
                  e.dataTransfer.setData('text/plain', b.id)
                  e.dataTransfer.effectAllowed = 'move'
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  setSobre(i)
                }}
                onDragLeave={() => setSobre((s) => (s === i ? null : s))}
                onDrop={(e) => {
                  e.preventDefault()
                  soltarEm(i)
                }}
                onDragEnd={() => {
                  arrastando.current = null
                  setSobre(null)
                }}
                className={`group flex items-center gap-1 rounded-lg pr-1 transition ${
                  selecionado === b.id ? 'bg-marca-50' : 'hover:bg-slate-50'
                } ${sobre === i ? 'shadow-[inset_0_2px_0_0_var(--color-marca-500)]' : ''} ${
                  b.oculto ? 'opacity-45' : ''
                }`}
              >
                <button
                  type="button"
                  onClick={() => onSelecionar(b.id)}
                  className={`flex min-w-0 flex-1 cursor-grab items-center gap-2 rounded-lg py-2 pl-2.5 text-left text-[13px] active:cursor-grabbing ${
                    selecionado === b.id ? 'font-medium text-marca-800' : 'text-slate-600'
                  }`}
                >
                  <span className="text-slate-300" aria-hidden="true">
                    ⠿
                  </span>
                  <span className="min-w-0 flex-1 truncate">{ROTULOS_BLOCO[b.tipo].nome}</span>
                  {problemasDoBloco && (
                    <span
                      className="flex size-4 shrink-0 items-center justify-center rounded-full bg-red-500 text-[10px] text-white"
                      title={problemasDoBloco.join(' · ')}
                    >
                      !
                    </span>
                  )}
                </button>

                {/* Os controles aparecem no hover — e ficam sempre visíveis
                    quando o bloco está oculto, que é o estado em que se
                    precisa deles para voltar atrás. */}
                <div
                  className={`flex shrink-0 items-center gap-0.5 transition ${
                    b.oculto ? '' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'
                  }`}
                >
                  <button
                    type="button"
                    title="Duplicar"
                    onClick={() => onDuplicar(b.id)}
                    className="flex size-6 items-center justify-center rounded text-slate-400 hover:bg-white hover:text-slate-700"
                  >
                    ⧉
                  </button>
                  <button
                    type="button"
                    title={b.oculto ? 'Mostrar' : 'Ocultar'}
                    onClick={() => onAlternarOculto(b.id)}
                    className="flex size-6 items-center justify-center rounded text-slate-400 hover:bg-white hover:text-slate-700"
                  >
                    {b.oculto ? '◌' : '●'}
                  </button>
                  <button
                    type="button"
                    title="Remover"
                    onClick={() => onRemover(b.id)}
                    className="flex size-6 items-center justify-center rounded text-slate-400 hover:bg-white hover:text-red-600"
                  >
                    ✕
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
        <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
          Arraste para reordenar, ou <kbd>Alt</kbd>+<kbd>↑</kbd>/<kbd>↓</kbd> no bloco selecionado.
          <br />
          <kbd>Ctrl</kbd>+<kbd>S</kbd> salva · <kbd>Ctrl</kbd>+<kbd>Z</kbd> desfaz · <kbd>Esc</kbd>{' '}
          tira a seleção.
        </p>
      </div>
    </aside>
  )
}
